import { Injectable } from '@angular/core';
import { DataIoService } from './data-io.service';
import { MenuService } from './menu.service';
import { TranslationService } from './translation.service';
import { Subject, Subscription, of } from 'rxjs';

export function wait(ms: number): Promise<boolean> {
  return new Promise<boolean>(r =>
    setTimeout(
      r,
      ms
    )
  );
}
export type AnimationStep = 'fadeIn' | 'fadeOut' | 'click' | 'all';

class Command {
  timeOffset: number = 0;
  command: string;
  args: Array<string> = new Array<string>();
  timeline_mode: boolean = true;
  constructor(_str: string) {
    let str = _str.trim();
    this.timeline_mode = (str.match(/^[0-9]*\s/g)) !== null;

    if (this.timeline_mode) {
      this.parseAsTimelineCommand(str);
    } else {
      this.parseAsBatchCommand(str);
    }

  }
  mode(): 'batch' | 'timeline' {
    return this.timeline_mode ? 'timeline' : 'batch';
  }
  parseAsBatchCommand(str: string) {
    let parts = str.split(/\s+/g).map((x: string) => x.trim());
    this.command = parts.shift();
    this.args = parts;
  }
  parseAsTimelineCommand(str: string) {
    let parts = str.split(/\s+/g).map((x: string) => x.trim());

    if (parts.length < 2) {
      console.warn("invalid command: " + str);
      return;
    }
    if (!this.isInt(parts[0])) {
      console.warn("invalid time-offset: " + parts[0]);
      return;
    }
    this.timeOffset = parseInt(parts.shift());
    if (!this.checkCommand(parts)) {
      console.warn("invalid command: " + parts.join(' '));
      return;
    }
    this.command = parts.shift();
    this.args = parts;
  }
  checkCommand(parts: Array<string>): boolean {
    return true;
  }
  isInt(value: any): boolean {
    return !isNaN(value) &&
      parseInt(Number(value) + "") == value &&
      !isNaN(parseInt(value, 10));
  }
}

@Injectable({
  providedIn: 'root'
})
export class CommandBatchService {

  CommandQueue: Array<Command> = new Array<Command>();
  _running: boolean = false;
  _running_since?: number = undefined;
  ticks?: Subject<Command> = new Subject<Command>();
  debug = (msg: any) => console.debug(`command-batch: "${msg}"`);
  constructor(
    private io: DataIoService,
    private menuService: MenuService,
    private translationService: TranslationService
  ) {
    this.debug("constructed.");
  }
  scriptEnv: any = {
    defaultWait: 1500,
    indicateKeyPresses: false,
    shortMultiTaps: false
  };
  getMetaCommand(str: string): Command | undefined {


    let metaElements = this.CommandQueue.filter((x: Command) => x.command === str);

    if (metaElements.length > 1) {
      alert("multiple " + str + "-statements.");
      throw new Error("multiple " + str + "-statements.");
    }
    this.CommandQueue = this.CommandQueue.filter((x: Command) => x.command !== str);
    if (metaElements.length === 1) {
      return metaElements[0];
    } else {
      return undefined;
    }


  }
  highlighted: string[] = new Array<string>();
  isHighlighted(str: string): boolean {
    return this.highlighted.includes(str);
  }

  applyModifiers(lines: string[]): string[] {
    let _lines = [];
    for (let l of lines) {
      if (l.startsWith('#')) {
        continue;
      }
      let match = l.match(/^[0-9]*x/);
      if (match !== undefined && Array.isArray(match) && match.length === 1) {
        let multiplier = parseInt(match[0].replace('x', ''));
        let rawCommand = l.replace(match[0], '');
        for (let i = 0; i < multiplier; i++) {
          _lines.push(rawCommand)
        }
      } else {
        _lines.push(l);
      }
    }
    return _lines;
  }
  runscript(str?: string) {
    console.log("Running script " + str);
    this.stop();

    this.CommandQueue.length = 0;
    this.createScript(str);

    wait(
      this.scriptEnv.waitTime + 150
    ).then(
      () => {
        this.start();
      }
    );

  }
  envChanged$: Subject<boolean> = new Subject<boolean>();
  createScript(_str?: string) {

    this.debug("Creating script from string " + _str);
    this.scriptEnv.defaultWait = undefined;
    let str = _str ?? '';
    str = str.replace(/^\s*$/g, '');
    let script = str.replace('\r\n', '\n');
    let commandLines: string[] = script.split('\n').map((x: string) => x.trim()).filter((x: string) => x.length > 0);
    commandLines = this.applyModifiers(commandLines);
    this.CommandQueue = commandLines.map((x: string) => (new Command(x)));
    this.scriptEnv.defaultWait = parseInt(this.getMetaCommand('defaultWait')?.args[0]) ?? this.scriptEnv.defaultWait;
    this.scriptEnv.indicateKeyPresses = (this.getMetaCommand('indicateKeyPresses') !== undefined);
    this.scriptEnv.shortMultiTaps = (this.getMetaCommand('shortMultiTaps') !== undefined);
    this.envChanged$.next(true);
    this.debug(this.scriptEnv);
    if (this.CommandQueue.length > 0) {

      let firstMode = this.CommandQueue[0].mode();
      if (!this.CommandQueue.every((v, i, a) => v.mode() === firstMode)) {
        alert("invalid script: mixed timeline with batch mode!");
        throw new Error("invalid script: mixed timeline with batch mode!");
      } else {
        this.debug("MODE is " + firstMode);
      }
    } else {
      this.debug("queue length isnt >0");
    }

    this._running_since = Date.now();
  }
  _lastCommand?: Command;
  _nextCommand?: Command;
  tick(previousCommand?: Command) {
    this._lastCommand = previousCommand;
    this.debug("tick.");
    if (this.CommandQueue.length == 0) {
      this.debug("CommandQueue is empty. stopping.");
      this.stop();
      return;
    }
    let cmd = this.CommandQueue.shift();
    this.debug("Firing command " + cmd.command);
    if (this.CommandQueue.length > 0) {
      this._nextCommand = this.CommandQueue[0];
    } else {
      this._nextCommand = undefined;
    }
    this.ticks.next(cmd);
  }
  getWaitTimeForCommand(cmd: Command): number {
    if (this.isImmediateCommand(cmd)) {
      return 0;
    }
    if (!this._running) {
      throw Error("pipeline not running.");
    }
    if (cmd.mode() == 'batch') {
      if (cmd.command === 'wait') {
        return parseInt(cmd.args[0]);
      }
      return this.scriptEnv.defaultWait ?? 0;
    }
    let current_offset = Date.now() - this._running_since;
    return Math.max(0, cmd.timeOffset - current_offset);
  }

  runSub?: Subscription;
  stop() {
    this._running = false;
    this._lastCommand = undefined;
    this._nextCommand = undefined;
    this.clearHighlights();
    this.runSub?.unsubscribe();
    this.runSub = undefined;
    this.startAnimation$.next('fadeOut');
  }
  handleTick(cmd: Command): void {
    if (this.isImmediateCommand(cmd)) {
      this.runCommand(cmd);
      this.tick(cmd);
      return;
    }
    if (cmd.command === 'wait') {
      setTimeout(() => {
        this.tick(cmd)
      }, parseInt(cmd.args[0]))
      return;
    }
    if (this.scriptEnv.indicateKeyPresses) {
      let animation_duration = this.scriptEnv.defaultWait;
      let key_press_wait = animation_duration * (1250 / 2250) + 60;

      let fadeInWait = 0;
      let clickWait = (1050 / 2250);
      let fadeOutWait = (1350 / 2250);
      let doFadeIn = !this.scriptEnv.shortMultiTaps || (this._lastCommand?.command != cmd.command);
      let doFadeOut = !this.scriptEnv.shortMultiTaps || (this._nextCommand?.command != cmd.command);
      let overall = animation_duration;
      if (!doFadeIn) {
        overall -= fadeInWait * animation_duration;
      }
      if (!doFadeOut) {
        overall -= fadeInWait * animation_duration;
      }
      Promise.all(
        [
          wait(overall),
          wait(animation_duration * fadeInWait).then(() => doFadeIn && this.indicatePress(cmd, 'fadeIn')),
          wait(animation_duration * clickWait).then(() => this.indicatePress(cmd, 'click')),
          wait(animation_duration * fadeOutWait).then(() => doFadeOut && this.indicatePress(cmd, 'fadeOut')),
          wait(key_press_wait).then(
            () => this.runCommand(cmd)
          )
        ]).then(
          () => this.tick(cmd)
        );
    } else {
      wait(this.scriptEnv.duration).then(() => this.runCommand(cmd)).then(() => this.tick(cmd));
    }

  }

  start() {
    this._running = true;
    this.runSub = this.ticks.subscribe(
      {
        next: (cmd: Command) => this.handleTick(cmd)
      }
    );
    this.tick(undefined);
  }


  isImmediateCommand(cmd: Command): boolean {
    return ['goto', 'setlang'].includes(cmd.command);
  }

  _kpTo = undefined;

  hasHighlight() {
    return this.highlighted.length > 0;
  }
  startAnimation$: Subject<AnimationStep> = new Subject<AnimationStep>();
  highlightedButton(): { [key: string]: boolean } {
    let classes: {
      [key: string]: boolean
    } = {};
    this.highlighted.forEach((b: string) => {
      classes[b] = true;
    });
    return classes;
  }
  startAnimation(cmd?: Command, animationStep?: AnimationStep) {
    //this.startAnimation$.next();
  }
  clearHighlights() {
    this.highlighted.length = 0;
  }
  indicatePress(cmd: Command, animationStep?: AnimationStep) {

    if (this.scriptEnv.indicateKeyPresses) {
      console.log(`indicating press for ${cmd.command} with animation-step ${animationStep}`)
      this.clearHighlights();
      this.highlighted.push(cmd.command);
      this.startAnimation$.next(animationStep ?? 'all');
    }
  }

  runCommand(cmd: Command) {
    let fn = {
      confirm: () => this.io.inputs.events$.next('confirm'),
      down: () => this.io.inputs.events$.next('down'),
      up: () => this.io.inputs.events$.next('up'),
      back: () => this.io.inputs.events$.next('back'),
      menu: () => this.io.inputs.events$.next('menu'),
      question: () => this.io.inputs.events$.next('question'),
      goto: () => this.menuService.goto(cmd.args[0]),
      setlang: () => { this.translationService.useLanguage(cmd.args[0]); }
    }[cmd.command];
    if (fn !== undefined) {
      fn();
    } else {
      console.warn("Undefined fn for " + cmd.command);
    }

  }
}

